import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Loader2, QrCode, Pill, ShoppingCart, User } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ScanLandingPage() {
  const { barcode } = useParams();
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);
  const [valid, setValid] = useState(false);

  useEffect(() => {
    const patientToken = localStorage.getItem('patient_token');
    const vendorToken = localStorage.getItem('vendor_token');
    const vendorUser = localStorage.getItem('vendor_user');

    // If chemist is logged in, redirect to vendor dashboard with rx param
    if (vendorToken && vendorUser) {
      try {
        const user = JSON.parse(vendorUser);
        if (user.role === 'chemist_admin') {
          navigate(`/vendor/dashboard?rx=${barcode}`, { replace: true });
          return;
        }
      } catch (err) { console.error(err); }
    }

    // If patient is logged in, redirect to marketplace with rx param
    if (patientToken) {
      navigate(`/patient/marketplace?rx=${barcode}`, { replace: true });
      return;
    }

    // Not logged in - verify prescription exists, then show login options
    const verify = async () => {
      try {
        const res = await fetch(`${API}/api/patient-marketplace/public/prescription-lookup/${barcode}`);
        const data = await res.json();
        setValid(data.found);
      } catch {
        setValid(false);
      }
      setChecking(false);
    };
    verify();
  }, [barcode, navigate]);

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center space-y-3">
          <Loader2 className="w-8 h-8 animate-spin text-teal-600 mx-auto" />
          <p className="text-slate-500">Verifying prescription...</p>
        </div>
      </div>
    );
  }

  if (!valid) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <Card className="max-w-sm w-full">
          <CardContent className="pt-6 text-center space-y-4">
            <QrCode className="w-12 h-12 text-slate-300 mx-auto" />
            <h2 className="text-lg font-bold text-slate-800">Prescription Not Found</h2>
            <p className="text-sm text-slate-500">This QR code does not match any prescription in our system.</p>
            <Button variant="outline" onClick={() => navigate('/')} data-testid="go-home-btn">Go Home</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-teal-50 to-white p-4">
      <Card className="max-w-sm w-full shadow-lg">
        <CardContent className="pt-6 space-y-5">
          <div className="text-center space-y-2">
            <div className="w-14 h-14 rounded-xl bg-teal-100 flex items-center justify-center mx-auto">
              <Pill className="w-7 h-7 text-teal-700" />
            </div>
            <h2 className="text-lg font-bold text-slate-800">Prescription Scanned</h2>
            <p className="text-sm text-slate-500">Login to continue ordering medicines</p>
          </div>

          <div className="space-y-3">
            <Button
              className="w-full bg-teal-600 hover:bg-teal-700"
              onClick={() => {
                localStorage.setItem('pending_rx_barcode', barcode);
                navigate('/patient/login');
              }}
              data-testid="login-patient-btn"
            >
              <ShoppingCart className="w-4 h-4 mr-2" />
              I'm a Patient - Order Medicines
            </Button>

            <Button
              variant="outline"
              className="w-full"
              onClick={() => {
                localStorage.setItem('pending_rx_barcode', barcode);
                navigate('/vendor/login');
              }}
              data-testid="login-chemist-btn"
            >
              <User className="w-4 h-4 mr-2" />
              I'm a Chemist - Start Billing
            </Button>
          </div>

          <p className="text-xs text-center text-slate-400">
            Prescription Code: <span className="font-mono">{barcode}</span>
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
